import { CancelablePromise, OpenAPI } from "../generated";
import type { ShipmentDocumentView } from "../generated";

export class ShipmentDocumentCustomService {
  /**
   * Upload a single document to a shipment.
   *
   * Uses a raw fetch (not the generated client) because the global
   * OpenAPI.HEADERS force `Content-Type: application/json`, which would strip
   * the multipart boundary and make Symfony see no file. Letting the browser
   * set the Content-Type keeps the boundary intact. Mirrors GpsrCustomService.
   */
  public static upload(
    shipmentId: string,
    file: File,
  ): CancelablePromise<ShipmentDocumentView> {
    const formData = new FormData();
    formData.append("file", file);

    return new CancelablePromise(async (resolve, reject) => {
      try {
        const response = await fetch(
          `${OpenAPI.BASE}/v2/api/shipment-tracker/shipments/${shipmentId}/documents`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: formData,
          },
        );

        const data = await response.json();
        if (!response.ok || data?.success === false) {
          reject(new Error(data?.error || "Upload failed"));
          return;
        }
        resolve(data as ShipmentDocumentView);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Fetch a document's bytes as a blob.
   *
   * Firebase Storage objects are private and FirebaseStorageService exposes no
   * signed/public URL, so every read is proxied through Symfony with the bearer
   * token. Both download and preview go through here.
   */
  public static async fetchBlob(
    shipmentId: string,
    docId: string,
  ): Promise<Blob> {
    const response = await fetch(
      `${OpenAPI.BASE}/v2/api/shipment-tracker/shipments/${shipmentId}/documents/${docId}/download`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Network response was not ok");
    }

    return response.blob();
  }

  /**
   * Download a shipment document.
   *
   * The download endpoint streams a private file (no public URL) and requires
   * the bearer token, so we fetch the blob directly and trigger a client-side
   * download using the original file name.
   */
  /**
   * Download every document of a shipment as one ZIP. The archive name comes from the
   * Content-Disposition header the server sets (container reference + date).
   */
  public static downloadAll(shipmentId: string): CancelablePromise<void> {
    return new CancelablePromise(async (resolve, reject) => {
      try {
        const response = await fetch(
          `${OpenAPI.BASE}/v2/api/shipment-tracker/shipments/${shipmentId}/documents/download-all`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || "Network response was not ok");
        }

        const disposition = response.headers.get("Content-Disposition") ?? "";
        const match = disposition.match(/filename="?([^";]+)"?/i);
        const filename = match ? match[1] : `${shipmentId}.zip`;

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  public static download(
    shipmentId: string,
    docId: string,
    originalName: string,
  ): CancelablePromise<void> {
    return new CancelablePromise(async (resolve, reject) => {
      try {
        const blob = await ShipmentDocumentCustomService.fetchBlob(
          shipmentId,
          docId,
        );

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = originalName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }
}
